import { computed } from 'vue'
import { useAuthStore } from '../stores/auth.js'
import { useProjectStore } from '../stores/projects.js'
import {
  POLICY_HIDDEN,
  isPrivilegedRole,
  rolesOfStatus,
  canRoleOperateStatus
} from '../utils/workflowAccess.js'

/**
 * Le fasi del processo viste dal profilo di chi sta guardando: cosa può operare, cosa vede soltanto,
 * come si chiamano i profili. Unico punto della regola lato client — i componenti non leggono mai
 * `status.roles` da soli.
 *
 * Il Backend riapplica le stesse regole su ogni scrittura (WorkflowAccessService): qui si decide
 * solo cosa mostrare, mai cosa è permesso davvero.
 */
export function useWorkflowAccess() {
  const authStore = useAuthStore()
  const projectStore = useProjectStore()

  /** Profilo dell'utente collegato. */
  const role = computed(() => authStore.user?.role || null)

  const isAdmin = computed(() => isPrivilegedRole(role.value))

  /** Profili configurati in cfg.json, senza admin e superadmin. */
  const roles = computed(() => projectStore.workflowRoles)

  /** Cosa succede ai processi fermi in una fase di un altro profilo: "readonly" o "hidden". */
  const policy = computed(() => projectStore.roleAccessPolicy)

  /** Vero solo quando almeno uno stato dichiara dei profili: senza, non c'è niente da filtrare. */
  const hasRoleScopes = computed(() =>
    projectStore.projectTypes.some(type =>
      (type.statusList || []).some(status => rolesOfStatus(status).length > 0))
  )

  const roleLabel = (id) => roles.value.find(r => r.id === id)?.label || id

  const statusOf = (projectTypeId, statusId) =>
    projectStore.getStatusInfo(String(statusId), projectTypeId)

  /** Etichette dei profili che operano una fase. Vuoto = fase di tutti. */
  const statusRoleLabels = (projectTypeId, statusId) =>
    rolesOfStatus(statusOf(projectTypeId, statusId)).map(roleLabel)

  const canOperateStatus = (projectTypeId, statusId, forRole = role.value) =>
    canRoleOperateStatus(forRole, statusOf(projectTypeId, statusId))

  /** Lo stesso, a partire da un processo della lista. */
  const canOperate = (project, forRole = role.value) =>
    !project || canOperateStatus(project.projectType, project.activeStatus, forRole)

  /** Con policy "hidden" si vede solo ciò che si può operare; altrimenti si vede tutto. */
  const isVisible = (project, forRole = role.value) =>
    policy.value !== POLICY_HIDDEN || canOperate(project, forRole)

  /**
   * Profili che possono lanciare un'azione IA (`roles` della voce in `aiActions`). Vuoto = azione
   * aperta a chiunque operi la fase. L'azione arriva già risolta da `resolveVerifications`, quindi
   * `roles` è quello della biblioteca.
   */
  const actionRoles = (action) =>
    Array.isArray(action?.roles) ? action.roles.filter(Boolean) : []

  const actionRoleLabels = (action) => actionRoles(action).map(roleLabel)

  /** Il profilo può premere il pulsante di questa azione? Il Backend riapplica la stessa regola. */
  const canRunAction = (action, forRole = role.value) => {
    if (isPrivilegedRole(forRole)) return true

    const declared = actionRoles(action)
    if (declared.length === 0) return true

    return !!forRole && declared.includes(forRole)
  }

  /** Gli id di stato che questo profilo può lavorare, per un tipo di processo. */
  const operableStatusIds = (projectTypeId, forRole = role.value) =>
    projectStore.getStatusListForType(projectTypeId)
      .filter(status => canRoleOperateStatus(forRole, status))
      .map(status => status.id)

  /**
   * C'è almeno una fase iniziale che questo profilo può lavorare? Le fasi iniziali sono quelle
   * marcate `isOptional`, o la prima della lista quando non ce ne sono. Aprire un processo in una
   * fase di qualcun altro significherebbe non poterlo più toccare, e il Backend rifiuta comunque
   * la creazione.
   */
  const canCreateProject = computed(() =>
    projectStore.projectTypes.some(type => {
      const list = type.statusList || []
      const optional = list.filter(s => s.isOptional === true)
      const initial = optional.length ? optional : list.slice(0, 1)
      return initial.some(status => canRoleOperateStatus(role.value, status))
    }))

  /** Gli stati che questo profilo può lavorare, oggetti interi, di tutti i tipi di processo. */
  const operableStatuses = (forRole = role.value) =>
    projectStore.projectTypes.flatMap(type =>
      (type.statusList || [])
        .filter(status => canRoleOperateStatus(forRole, status))
        .map(status => ({ ...status, projectType: type.id, projectTypeDescr: type.descr }))
    )

  return {
    role,
    isAdmin,
    roles,
    policy,
    hasRoleScopes,
    roleLabel,
    statusRoleLabels,
    canOperateStatus,
    canOperate,
    canRunAction,
    actionRoleLabels,
    canCreateProject,
    isVisible,
    operableStatusIds,
    operableStatuses
  }
}
